// src/components/black-friday/search-term-comparison-view.tsx
import React, { useState, useMemo } from "react";
import {
  BFSearchTermData,
  BFPMaxCategoryData,
  BFSearchTermComparison,
} from "@/types/black-friday";
import {
  createSearchTermComparison,
  filterSearchTermComparisons,
  sortSearchTermComparisons,
} from "@/lib/black-friday-search-term-comparison";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpIcon, ArrowDownIcon, ArrowUpDown, Info, Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { ComparisonPeriod } from "./dashboard-view";

interface Props {
  searchTerms25: BFSearchTermData[];
  searchTerms24: BFSearchTermData[];
  searchTerms23: BFSearchTermData[];
  pMaxCategories25: BFPMaxCategoryData[];
  pMaxCategories24: BFPMaxCategoryData[];
  pMaxCategories23: BFPMaxCategoryData[];
  comparisonPeriod: ComparisonPeriod;
}

type ChangeDisplayMode = "absolute" | "percentage" | "both";
type FilterMode = "all" | "new" | "existing" | "missing";
type CampaignTypeFilter = "all" | "search_shopping" | "pmax";

export default function SearchTermComparisonView({
  searchTerms25,
  searchTerms24,
  searchTerms23,
  pMaxCategories25,
  pMaxCategories24,
  pMaxCategories23,
  comparisonPeriod,
}: Props) {
  const [changeMode, setChangeMode] = useState<ChangeDisplayMode>("percentage");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<CampaignTypeFilter>("all");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [limit, setLimit] = useState<number>(100);
  const [sortColumn, setSortColumn] = useState<string>("impressions25");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Derive comparison year from period
  const comparisonYear = comparisonPeriod.startsWith('year2024') ? '2024' : '2023';

  // Create comparison data
  const comparisons = useMemo(() => {
    // Filter by campaign type
    let search25 = searchTerms25;
    let pMax25 = pMaxCategories25;
    let searchComp = comparisonYear === "2024" ? searchTerms24 : searchTerms23;
    let pMaxComp = comparisonYear === "2024" ? pMaxCategories24 : pMaxCategories23;

    if (campaignTypeFilter === "search_shopping") {
      pMax25 = [];
      pMaxComp = [];
    } else if (campaignTypeFilter === "pmax") {
      search25 = [];
      searchComp = [];
    }

    const compared = createSearchTermComparison(
      search25,
      pMax25,
      searchComp,
      pMaxComp
    );

    // Filter by new/existing/missing
    let filtered = filterSearchTermComparisons(compared, filterMode);

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(term =>
        term.searchTerm.toLowerCase().includes(searchLower)
      );
    }

    const sorted = sortSearchTermComparisons(filtered, sortColumn, sortDirection);
    return sorted;
  }, [
    searchTerms25,
    searchTerms24,
    searchTerms23,
    pMaxCategories25,
    pMaxCategories24,
    pMaxCategories23,
    comparisonPeriod,
    campaignTypeFilter,
    searchFilter,
    filterMode,
    sortColumn,
    sortDirection,
  ]);

  const displayTerms = comparisons.slice(0, limit);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Helper to format change values
  const formatChange = (
    absolute: number,
    percentage: number,
    isPositiveGood: boolean = true
  ) => {
    const absFormatted =
      absolute >= 0
        ? `+${Math.round(absolute).toLocaleString()}`
        : Math.round(absolute).toLocaleString();
    const pctFormatted =
      percentage >= 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;

    let colorClass = "";
    if (Math.abs(percentage) < 0.1) {
      colorClass = "text-gray-500";
    } else if (isPositiveGood) {
      colorClass = absolute > 0 ? "text-green-600" : "text-red-600";
    } else {
      colorClass = absolute > 0 ? "text-red-600" : "text-green-600";
    }

    const indicator =
      absolute > 0 ? (
        <ArrowUpIcon className="w-3 h-3 inline" />
      ) : absolute < 0 ? (
        <ArrowDownIcon className="w-3 h-3 inline" />
      ) : null;

    if (changeMode === "absolute") {
      return (
        <span className={colorClass}>
          {indicator} {absFormatted}
        </span>
      );
    } else if (changeMode === "percentage") {
      return (
        <span className={colorClass}>
          {indicator} {pctFormatted}
        </span>
      );
    } else {
      return (
        <span className={colorClass}>
          {indicator} {absFormatted} ({pctFormatted})
        </span>
      );
    }
  };

  // Render sort icon
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUpIcon className="w-4 h-4 ml-1 inline" />
    ) : (
      <ArrowDownIcon className="w-4 h-4 ml-1 inline" />
    );
  };

  // Empty state
  if (!searchTerms25 || searchTerms25.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="text-center space-y-4">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-semibold">No Search Term Data Available</h3>
            <p className="text-muted-foreground max-w-md">
              Run the Black Friday script (v1.1.0+) in your Google Ads account to collect search term data.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap gap-4">
        {/* Change Display Mode */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Show change:</label>
          <Select
            value={changeMode}
            onValueChange={(value) => setChangeMode(value as ChangeDisplayMode)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="absolute">Absolute</SelectItem>
              <SelectItem value="percentage">Percentage</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Filter Mode */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Filter:</label>
          <Select
            value={filterMode}
            onValueChange={(value) => setFilterMode(value as FilterMode)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Terms</SelectItem>
              <SelectItem value="new">New (2025 only)</SelectItem>
              <SelectItem value="existing">Existing (both years)</SelectItem>
              <SelectItem value="missing">Missing (no longer seen)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaign Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Campaign type:</label>
          <Select
            value={campaignTypeFilter}
            onValueChange={(value) => setCampaignTypeFilter(value as CampaignTypeFilter)}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="search_shopping">Search & Shopping</SelectItem>
              <SelectItem value="pmax">Performance Max</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Search Filter */}
        <div className="flex items-center gap-2 flex-1 min-w-[250px]">
          <label className="text-sm font-medium">Search:</label>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Filter terms..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Limit */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Show:</label>
          <Select
            value={limit.toString()}
            onValueChange={(value) => setLimit(parseInt(value, 10))}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">Top 50</SelectItem>
              <SelectItem value="100">Top 100</SelectItem>
              <SelectItem value="250">Top 250</SelectItem>
              <SelectItem value="500">Top 500</SelectItem>
              <SelectItem value={comparisons.length.toString()}>
                All ({comparisons.length})
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary and Legend */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4" />
          <span>
            Showing {displayTerms.length} of {comparisons.length} search terms
            {filterMode !== "all" && ` (filtered: ${filterMode})`}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="underline decoration-dotted cursor-help ml-2">
                  Includes PMax search categories
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p>
                  This view combines Search & Shopping search terms with Performance Max search categories.
                  Terms are matched by name across years and metrics are aggregated.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-gray-200 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded"></div>
            <span className="text-xs">Performance Max (no cost data)</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {/* Search Term Column - Sticky */}
                <TableHead
                  className="sticky left-0 z-20 bg-white dark:bg-gray-950 min-w-[250px] max-w-[400px] cursor-pointer"
                  onClick={() => handleSort("searchTerm")}
                >
                  <div className="flex items-center">
                    Search Term / Category
                    {renderSortIcon("searchTerm")}
                  </div>
                </TableHead>

                {/* 2025 Metrics */}
                <TableHead
                  colSpan={4}
                  className="text-center bg-blue-50 dark:bg-blue-950/20 border-x"
                >
                  2025 Performance (so far)
                </TableHead>

                {/* Comparison Year Metrics */}
                <TableHead
                  colSpan={4}
                  className="text-center bg-gray-50 dark:bg-gray-900 border-x"
                >
                  {comparisonYear} Performance (full 12-week period)
                </TableHead>

                {/* Change Metrics */}
                <TableHead
                  colSpan={4}
                  className="text-center bg-amber-50 dark:bg-amber-950/20 border-l"
                >
                  Change (2025 vs {comparisonYear})
                </TableHead>
              </TableRow>
              <TableRow>
                {/* Empty cell for Search Term column */}
                <TableHead className="sticky left-0 z-20 bg-white dark:bg-gray-950"></TableHead>

                {/* 2025 Metrics */}
                <TableHead
                  className="text-right cursor-pointer bg-blue-50 dark:bg-blue-950/20"
                  onClick={() => handleSort("impressions25")}
                >
                  Impr {renderSortIcon("impressions25")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-blue-50 dark:bg-blue-950/20"
                  onClick={() => handleSort("cost25")}
                >
                  Cost {renderSortIcon("cost25")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-blue-50 dark:bg-blue-950/20"
                  onClick={() => handleSort("conv25")}
                >
                  Conv {renderSortIcon("conv25")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-blue-50 dark:bg-blue-950/20 border-r"
                  onClick={() => handleSort("convValue25")}
                >
                  Conv Val {renderSortIcon("convValue25")}
                </TableHead>

                {/* Comparison Year Metrics */}
                <TableHead
                  className="text-right cursor-pointer bg-gray-50 dark:bg-gray-900"
                  onClick={() => handleSort("impressionsComp")}
                >
                  Impr {renderSortIcon("impressionsComp")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-gray-50 dark:bg-gray-900"
                  onClick={() => handleSort("costComp")}
                >
                  Cost {renderSortIcon("costComp")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-gray-50 dark:bg-gray-900"
                  onClick={() => handleSort("convComp")}
                >
                  Conv {renderSortIcon("convComp")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-gray-50 dark:bg-gray-900 border-r"
                  onClick={() => handleSort("convValueComp")}
                >
                  Conv Val {renderSortIcon("convValueComp")}
                </TableHead>

                {/* Change Metrics */}
                <TableHead
                  className="text-right cursor-pointer bg-amber-50 dark:bg-amber-950/20"
                  onClick={() => handleSort("impressionsChange")}
                >
                  Impr Δ {renderSortIcon("impressionsChange")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-amber-50 dark:bg-amber-950/20"
                  onClick={() => handleSort("costChange")}
                >
                  Cost Δ {renderSortIcon("costChange")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-amber-50 dark:bg-amber-950/20"
                  onClick={() => handleSort("convChange")}
                >
                  Conv Δ {renderSortIcon("convChange")}
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer bg-amber-50 dark:bg-amber-950/20"
                  onClick={() => handleSort("convValueChange")}
                >
                  Conv Val Δ {renderSortIcon("convValueChange")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayTerms.map((term, idx) => {
                // PMax-only rows (no cost data) get subtle grey background
                const isPMaxOnly = !term.hasCostData25 && term.hasCurrentData;
                const rowBgClass = isPMaxOnly ? "bg-gray-200/70 dark:bg-gray-800/30" : "";
                const stickyBgClass = isPMaxOnly ? "bg-gray-200/70 dark:bg-gray-800/30" : "bg-white dark:bg-gray-950";

                return (
                  <TableRow key={idx} className={rowBgClass}>
                    {/* Search Term - Sticky */}
                    <TableCell className={`sticky left-0 z-10 ${stickyBgClass} font-medium min-w-[250px] max-w-[400px]`}>
                      <div className="line-clamp-2" title={term.searchTerm}>
                        {term.searchTerm}
                        {!term.hasComparisonData && (
                          <span className="ml-2 text-xs font-semibold text-blue-600">[N]</span>
                        )}
                      </div>
                    </TableCell>

                    {/* 2025 Metrics */}
                    <TableCell className="text-right bg-blue-50/50 dark:bg-blue-950/10">
                      {term.impressions25.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right bg-blue-50/50 dark:bg-blue-950/10">
                      {term.hasCostData25 ? (
                        `$${term.cost25.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-blue-50/50 dark:bg-blue-950/10">
                      {term.conv25.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </TableCell>
                    <TableCell className="text-right bg-blue-50/50 dark:bg-blue-950/10 border-r">
                      ${term.convValue25.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>

                    {/* Comparison Year Metrics */}
                    <TableCell className="text-right bg-gray-50/50 dark:bg-gray-900/50">
                      {term.impressionsComp.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right bg-gray-50/50 dark:bg-gray-900/50">
                      {term.hasCostDataComp ? (
                        `$${term.costComp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-gray-50/50 dark:bg-gray-900/50">
                      {term.convComp.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </TableCell>
                    <TableCell className="text-right bg-gray-50/50 dark:bg-gray-900/50 border-r">
                      ${term.convValueComp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>

                    {/* Change Metrics */}
                    <TableCell className="text-right bg-amber-50/50 dark:bg-amber-950/10">
                      {formatChange(
                        term.impressionsChange,
                        term.impressionsChangePct,
                        true
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-amber-50/50 dark:bg-amber-950/10">
                      {(term.hasCostData25 || term.hasCostDataComp) ? (
                        formatChange(term.costChange, term.costChangePct, false)
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right bg-amber-50/50 dark:bg-amber-950/10">
                      {formatChange(term.convChange, term.convChangePct, true)}
                    </TableCell>
                    <TableCell className="text-right bg-amber-50/50 dark:bg-amber-950/10">
                      {formatChange(
                        term.convValueChange,
                        term.convValueChangePct,
                        true
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
